// apps/web/src/components/workspaces/WorkspaceManager.tsx
// Workspace management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Workspace } from '../../types/workspace';
import { apiClient } from '../../lib/apiClient';

export function WorkspaceManager() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/workspaces');
        setWorkspaces(response.data.workspaces);
      } catch (err) {
        setError('Failed to load workspaces');
        console.error('Error loading workspaces:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspaces();
  }, []);

  const handleCreateWorkspace = async (workspaceData: any) => {
    try {
      const response = await apiClient.post('/api/v1/workspaces', workspaceData);
      setWorkspaces([response.data, ...workspaces]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create workspace');
      console.error('Error creating workspace:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading workspaces...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="workspace-manager">
      <div className="header">
        <h2>Workspace Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Workspace'}
        </button>
      </div>

      {showCreateForm && (
        <WorkspaceForm 
          onSubmit={handleCreateWorkspace} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="workspaces-grid">
        {workspaces.map(workspace => (
          <WorkspaceCard key={workspace.id} workspace={workspace} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function WorkspaceCard({ workspace }: { workspace: Workspace }) {
  return (
    <div className="workspace-card">
      <div className="workspace-header">
        <h3>{workspace.name}</h3>
        <span className="workspace-status">{workspace.status}</span>
      </div>
      <p>{workspace.description || 'No description'}</p>
      <p><strong>Owner:</strong> {workspace.ownerId.substring(0, 8)}...</p>
      <p><strong>Teams:</strong> {workspace.teamCount || 0}</p>
      <p><strong>Created:</strong> {new Date(workspace.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function WorkspaceForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ownerId: '',
    status: 'ACTIVE'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="workspace-form">
      <h3>Create New Workspace</h3>
      <input
        type="text"
        placeholder="Workspace Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <input
        type="text"
        placeholder="Owner ID"
        value={formData.ownerId}
        onChange={(e) => setFormData({...formData, ownerId: e.target.value})}
        required
      />
      <select
        value={formData.status}
        onChange={(e) => setFormData({...formData, status: e.target.value})}
      >
        <option value="ACTIVE">Active</option>
        <option value="INACTIVE">Inactive</option>
        <option value="ARCHIVED">Archived</option>
      </select>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Workspace</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}